import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowUp,
  LayoutGrid,
  Loader2,
  Maximize,
  Maximize2,
  Mic,
  Minimize,
  Pin,
  PinOff,
  RefreshCw,
  Sparkles,
  Sun,
  X,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  askAssistant,
  getBriefing,
  type AssistantMessage,
  type AssistantReply,
} from '@/lib/assistant';
import { AnswerCards, hasRenderedData } from './answer-cards';
import { CommandPalette } from './command-palette';
import { PlanCard } from './assistant-panel';
import { RichText } from './rich-text';
import { AttentionCards } from './attention-cards';
import {
  addWidget,
  loadBlocks,
  loadWidgets,
  removeWidget,
  clearWidgets,
  saveBlocks,
  type Widget,
} from './widget-store';
import { conversationKeys } from './conversation-history';
import { useDictation } from './use-dictation';

/** One answer, kept on the canvas until it is dismissed. */
interface Block {
  id: number;
  question: string;
  reply: AssistantReply;
}

const STARTERS = [
  'Break our headcount down by department',
  'Who is away in the next two weeks?',
  'Show me overdue tasks',
  'What roles are we hiring for?',
];

/**
 * The assistant as a workspace rather than a conversation.
 *
 * The side panel is the right shape when you are already somewhere and want to
 * ask about it. This is the other half: you start from what you want, and the
 * page assembles itself around the answer.
 *
 * Two things follow from that, and they are the whole design. Answers are blocks
 * you keep or dismiss, not messages in a log — so you can put headcount and who
 * is away side by side and look at them together, which a transcript cannot do.
 * And the resting state is the briefing, so the page opens with work on it
 * instead of an empty box asking what you want.
 */
export function AiView() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  // Restored from the last visit: leaving the page and coming back used to wipe
  // whatever you had just asked, which made the view feel disposable.
  const [blocks, setBlocks] = useState<Block[]>(() => loadBlocks() as unknown as Block[]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [showCommands, setShowCommands] = useState(false);
  const [briefing, setBriefing] = useState<AssistantReply | null>(null);
  const [briefingBusy, setBriefingBusy] = useState(true);
  const [widgets, setWidgets] = useState<Widget[]>(() => loadWidgets());
  const [confirmClear, setConfirmClear] = useState(false);
  /** The widget being shown full size, or null. Held as the widget rather than
   *  its id so the dialog title survives the card being unpinned underneath it. */
  const [expanded, setExpanded] = useState<Widget | null>(null);
  /** Whether the expanded panel has taken the whole screen. */
  const [fullscreen, setFullscreen] = useState(false);

  /**
   * Native fullscreen is asked for, not assumed. It needs a user gesture and can
   * be refused outright — an iframe without `allowfullscreen`, a locked-down
   * browser — so the panel takes the viewport on its own and the browser's own
   * chrome going away is the bonus. A refusal costs the toolbar, not the feature.
   */
  const toggleFullscreen = () => {
    const next = !fullscreen;
    setFullscreen(next);
    if (next) void document.documentElement.requestFullscreen?.().catch(() => {});
    else if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => {});
  };

  // Fullscreen can be left without touching our button — Escape, F11, the
  // browser's own control — and the panel must not be left claiming a screen it
  // no longer has.
  useEffect(() => {
    const sync = () => {
      if (!document.fullscreenElement) setFullscreen(false);
    };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  const [widgetData, setWidgetData] = useState<Record<string, AssistantReply | 'loading'>>({});
  const nextId = useRef(1);
  const conversationId = useRef<string | undefined>(undefined);

  // The briefing runs on arrival rather than on a button: the point of this view
  // is that it has already looked. One run per mount, not per render.
  useEffect(() => {
    saveBlocks(blocks as unknown as ReturnType<typeof loadBlocks>);
  }, [blocks]);

  useEffect(() => {
    const sync = () => setWidgets(loadWidgets());
    window.addEventListener('hygge:widgets-changed', sync);
    return () => window.removeEventListener('hygge:widgets-changed', sync);
  }, []);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        setBriefing(await getBriefing());
      } catch {
        // A failed briefing is not worth an error state on an otherwise usable
        // page — the command bar still works.
        setBriefing(null);
      } finally {
        setBriefingBusy(false);
      }
    })();
  }, []);

  /** Re-asks a widget's question. A widget stores the question, so what it shows
   *  is current rather than a snapshot of when it was pinned. */
  const runWidget = async (widget: Widget) => {
    setWidgetData((prev) => ({ ...prev, [widget.id]: 'loading' }));
    try {
      const reply = await askAssistant([{ role: 'user', content: widget.prompt }]);
      setWidgetData((prev) => ({ ...prev, [widget.id]: reply }));
    } catch {
      setWidgetData((prev) => {
        const next = { ...prev };
        delete next[widget.id];
        return next;
      });
    }
  };

  useEffect(() => {
    for (const w of widgets) {
      if (widgetData[w.id] === undefined) void runWidget(w);
    }
    // Keyed on the widget list alone: including widgetData would re-run the
    // effect with every result it stores, which is a loop.
  }, [widgets]);

  const pin = (block: Block) => {
    setWidgets(addWidget(block.question, block.question));
  };

  const ask = async (text: string) => {
    const question = text.trim();
    if (!question || busy) return;
    setInput('');
    setBusy(true);
    setShowCommands(false);

    // Only the current question goes up: blocks are independent answers, not a
    // thread, so carrying the previous ones as history would be a lie about what
    // the user is looking at.
    const messages: AssistantMessage[] = [{ role: 'user', content: question }];
    try {
      const reply = await askAssistant(messages, undefined, conversationId.current);
      conversationId.current = reply.conversationId ?? conversationId.current;
      setBlocks((prev) => [{ id: nextId.current++, question, reply }, ...prev]);
      void qc.invalidateQueries({ queryKey: conversationKeys.all });
    } catch (err) {
      setBlocks((prev) => [
        {
          id: nextId.current++,
          question,
          reply: {
            text: err instanceof Error ? `That request failed: ${err.message}` : 'That request failed.',
            proposals: [],
            toolsUsed: [],
          },
        },
        ...prev,
      ]);
    } finally {
      setBusy(false);
    }
  };

  // Spoken commands are sent as soon as the speaker stops, rather than dropped
  // in the box to be pressed. Saying it and then reaching for the keyboard is
  // not a voice command. Nothing is written on the strength of a transcript
  // either way: a misheard instruction still arrives as a card to approve.
  const dictation = useDictation({ onFinal: (text) => void ask(text) });


  const firstName = user?.employee?.firstName ?? '';
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  });
  // How many findings the briefing came back with, for the count beside the heading.
  const briefingCount = briefing
    ? briefing.text.split('\n').filter((l) => /^\s*(?:[-*•]|\d+[.)])\s+/.test(l)).length
    : 0;

  return (
    /**
     * Two widths on one page.
     *
     * Asking and reading want a column you can follow — a question box and a
     * paragraph of narration stretched across a 27" monitor are worse, not
     * better. Pinned widgets want the opposite: they are a dashboard, and a
     * dashboard that scrolls one card at a time wastes the screen it is on. So
     * the conversation keeps its reading measure and the widget grid takes the
     * whole page.
     */
    <div className="mx-auto w-full max-w-[1600px] px-6 pb-16 pt-10">
      <div className="mx-auto w-full max-w-5xl">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-foreground">
            What do you need{firstName ? `, ${firstName}` : ''}?
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Ask across people, absences, projects and boards — answers come back drawn, not described.
          </p>
        </div>
        <p className="hidden shrink-0 text-xs tabular-nums text-muted-foreground sm:block">{today}</p>
      </div>

      {/* The command bar is the page's centre of gravity, so it gets the accent
          ring and the height. Everything else is quieter than it. */}
      <div className="rounded-2xl border border-border bg-card/40 transition-colors focus-within:border-hygge/50 focus-within:ring-4 focus-within:ring-hygge/10">
        <div className="flex items-start gap-3 px-4 pt-4">
          <Sparkles className="mt-1 h-4 w-4 shrink-0 text-hygge" />
          <Textarea
            value={dictation.listening ? dictation.transcript : input}
            onChange={(e) => setInput(e.target.value)}
            readOnly={dictation.listening}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void ask(input);
              }
            }}
            placeholder={dictation.listening ? 'Listening…' : 'Ask anything, or say what to change…'}
            rows={2}
            className="resize-none border-0 bg-transparent p-0 text-[15px] leading-relaxed shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="flex items-center gap-2 px-4 pb-3 pt-2">
          <button
            type="button"
            onClick={() => setShowCommands((v) => !v)}
            className={cn(
              'flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
              showCommands && 'border-hygge/40 bg-hygge/10 text-foreground',
            )}
            aria-pressed={showCommands}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Commands
          </button>

          {/* Offered only where the browser actually provides it. A microphone
              button that does nothing on Firefox is worse than no button. */}
          {dictation.supported && (
            <button
              type="button"
              onClick={() => (dictation.listening ? dictation.stop() : dictation.start())}
              disabled={busy}
              aria-pressed={dictation.listening}
              aria-label={dictation.listening ? 'Stop dictating' : 'Dictate a command'}
              className={cn(
                'flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40',
                dictation.listening && 'border-hygge/40 bg-hygge/10 text-foreground',
              )}
            >
              {dictation.listening ? (
                <>
                  {/* A live microphone should look live. */}
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-hygge opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-hygge" />
                  </span>
                  Listening
                </>
              ) : (
                <>
                  <Mic className="h-3.5 w-3.5" />
                  Speak
                </>
              )}
            </button>
          )}
          {dictation.error && (
            <span className="text-[11px] text-destructive">{dictation.error}</span>
          )}

          <button
            type="button"
            onClick={() => void ask(input)}
            disabled={busy || !input.trim()}
            aria-label="Send"
            className="ml-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-hygge text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {showCommands ? (
        <div className="mt-4 rounded-xl border border-border p-3">
          <CommandPalette busy={busy} onRun={(prompt) => void ask(prompt)} />
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {STARTERS.map((s) => (
            <button
              key={s}
              type="button"
              disabled={busy}
              onClick={() => void ask(s)}
              className="cursor-pointer rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-hygge/40 hover:bg-hygge/5 hover:text-foreground disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* One column, full width. Tiling answers side by side sounded good and made
          a single chart half as wide — and width was the whole reason this view
          exists. */}
      {blocks.length > 0 && (
        <section className="mt-10 space-y-3">
          {blocks.map((block) => (
            <article
              key={block.id}
              className="overflow-hidden rounded-xl border border-border bg-card/40"
            >
              <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-hygge" />
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {block.question}
                </span>
                <button
                  type="button"
                  onClick={() => pin(block)}
                  aria-label={`Pin ${block.question} as a widget`}
                  title="Pin as widget"
                  className="shrink-0 cursor-pointer rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-hygge"
                >
                  <Pin className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setBlocks((prev) => prev.filter((b) => b.id !== block.id))}
                  aria-label={`Dismiss answer to ${block.question}`}
                  className="shrink-0 cursor-pointer rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </header>
              <div className="p-4">
                <RichText text={block.reply.text} compact={hasRenderedData(block.reply.data)} />
                <AnswerCards data={block.reply.data} />
                {block.reply.proposals.length > 0 && <PlanCard proposals={block.reply.proposals} />}
              </div>
            </article>
          ))}
        </section>
      )}
      </div>

      {widgets.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-3 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            <Pin className="h-3.5 w-3.5 text-hygge" />
            Widgets
            <span className="tabular-nums opacity-60">{widgets.length}</span>
            {/* Two presses rather than a dialog: rebuilding a dashboard means
                clearing it often, and a modal for something this cheap to redo
                is friction. The second press is still a deliberate one. */}
            <button
              type="button"
              onClick={() => {
                if (confirmClear) {
                  setWidgets(clearWidgets());
                  setConfirmClear(false);
                } else {
                  setConfirmClear(true);
                }
              }}
              onBlur={() => setConfirmClear(false)}
              className={cn(
                'ml-auto cursor-pointer rounded-md border border-border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.08em] transition-colors hover:bg-muted hover:text-foreground',
                confirmClear && 'border-destructive/50 text-destructive',
              )}
            >
              {confirmClear ? `Clear ${widgets.length}?` : 'Clear all'}
            </button>
          </h2>
          {/* Masonry rather than a row grid: widgets are wildly different
              heights — a four-row list next to a twelve-row chart — and equal
              row heights would leave half the dashboard empty. Columns pack
              them by height instead, which is why `break-inside-avoid` matters:
              without it a card is sliced across the column boundary. */}
          <div className="columns-1 gap-4 lg:columns-2 2xl:columns-3">
          {widgets.map((w) => {
            const state = widgetData[w.id];
            return (
              <article
                key={w.id}
                className="mb-4 break-inside-avoid overflow-hidden rounded-xl border border-border bg-card/40"
              >
                <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
                  <Pin className="h-3.5 w-3.5 shrink-0 text-hygge" />
                  <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
                    {w.title}
                  </span>
                  <button
                    type="button"
                    onClick={() => setExpanded(w)}
                    aria-label={`Open ${w.title} full size`}
                    className="shrink-0 cursor-pointer rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Maximize2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void runWidget(w)}
                    aria-label={`Refresh ${w.title}`}
                    className="shrink-0 cursor-pointer rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <RefreshCw className={cn('h-3.5 w-3.5', state === 'loading' && 'animate-spin')} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setWidgets(removeWidget(w.id))}
                    aria-label={`Remove ${w.title}`}
                    className="shrink-0 cursor-pointer rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <PinOff className="h-3.5 w-3.5" />
                  </button>
                </header>
                <div className="p-4">
                  {state === 'loading' || state === undefined ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-hygge" />
                      Refreshing…
                    </div>
                  ) : (
                    <>
                      <RichText text={state.text} compact={hasRenderedData(state.data)} />
                      <AnswerCards data={state.data} />
                    </>
                  )}
                </div>
              </article>
            );
          })}
          </div>
        </section>
      )}

      {/* Full size. The widget's own answer is re-rendered on a wide surface
          rather than scaled up — every chart in here sizes itself to its
          container, so the same markup that folds into a dashboard column
          unfolds here without a second layout. */}
      <Dialog
        open={expanded !== null}
        onOpenChange={(open) => {
          if (open) return;
          setExpanded(null);
          if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => {});
          setFullscreen(false);
        }}
      >
        <DialogContent
          className={cn(
            // A column, not the default grid. Given a fixed height the grid
            // stretches its rows to fill it, so the title floated in the middle
            // of a 132px band with dead space under it. A column lets the header
            // take what it needs and the answer take the rest.
            'flex flex-col',
            fullscreen
              ? 'h-screen max-h-screen w-screen max-w-none rounded-none border-0'
              : 'max-h-[92vh] w-[96vw] max-w-[1400px]',
          )}
          // In fullscreen the first Escape gives the screen back rather than
          // closing the widget — losing both at once is one press too many.
          onEscapeKeyDown={(event) => {
            if (!fullscreen) return;
            event.preventDefault();
            if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => {});
            setFullscreen(false);
          }}
        >
          {/* Padded past both buttons, which sit in the corner rather than in
              this line — see below. */}
          <DialogTitle className="truncate pr-20 text-sm font-medium">{expanded?.title}</DialogTitle>
          {/* Positioned like the close button rather than laid out in the title
              row: the two belong together, and a button in the text flow lands
              at the flow's baseline and short of the padding the close button is
              absolutely placed over — which read as one adrift from the other. */}
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={fullscreen ? 'Leave fullscreen' : 'Fill the screen'}
            className="absolute right-10 top-4 cursor-pointer rounded-sm text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            {fullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          </button>
          {/* The title stays put and only the answer scrolls — on a screen-high
              panel, scrolling the heading away is losing the label. */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {expanded &&
              (() => {
                const state = widgetData[expanded.id];
                if (state === 'loading' || state === undefined) {
                  return (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-hygge" />
                      Refreshing…
                    </div>
                  );
                }
                return (
                  <>
                    <RichText text={state.text} compact={hasRenderedData(state.data)} />
                    <AnswerCards data={state.data} />
                  </>
                );
              })()}
          </div>
        </DialogContent>
      </Dialog>

      <section className="mt-10">
        <h2 className="mb-3 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          <Sun className="h-3.5 w-3.5 text-hygge" />
          Needs your attention
          {briefingCount > 0 && (
            <span className="rounded-full bg-hygge/15 px-1.5 py-px font-mono text-[10px] tabular-nums text-hygge">
              {briefingCount}
            </span>
          )}
        </h2>

        {briefingBusy ? (
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-border px-4 py-6 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-hygge" />
            Looking across your workspace…
          </div>
        ) : briefing ? (
          <>
            <AttentionCards text={briefing.text} />
            <AnswerCards data={briefing.data} />
            {briefing.proposals.length > 0 && <PlanCard proposals={briefing.proposals} />}
          </>
        ) : (
          <p className="rounded-xl border border-dashed border-border px-4 py-6 text-xs text-muted-foreground">
            Nothing to report right now.
          </p>
        )}
      </section>

    </div>
  );
}
